import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import { Center } from '@/components/ui/center'
import { Spinner } from '@/components/ui/spinner'
import useDefaultToast from '@/components/useDefaultToast'
import useWebBackButton from '@/lib/hooks/useWebBackButton'
import FormCooperators from '@/modules/formSettings/FormCooperators'
import { selectFormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import {
  fetchFormCooperatorsThunk,
  selectFormCooperatorsByFormToken,
  selectFormCooperatorsQueryStateByFormToken,
} from '@/redux/slices/formCooperatorSlice'
import {
  fetchFormRolesThunk,
  selectFormRolesByFormToken,
  selectFormRolesQueryStateByFormToken,
} from '@/redux/slices/formRoleSlice'
import { formSelectors } from '@/redux/slices/formSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { Stack, useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'

export default function FormCooperatorsPage() {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()
  const { formToken } = useLocalSearchParams<{ formToken: string }>()
  const headerLeftItems = useWebBackButton(`/forms/${formToken}` as any)
  const [reloading, setReloading] = useState(false)

  useEffect(() => {
    dispatch(fetchFormRolesThunk({ formToken, first: 100 }))
    dispatch(fetchFormCooperatorsThunk({ formToken, first: 10, reset: true }))
  }, [formToken, dispatch])

  const currentBillingAccountId = useAppSelector(state => state.currentUser.user?.billingAccount.id)
  const formCooperatorsQueryState = useAppSelector(state =>
    selectFormCooperatorsQueryStateByFormToken(state, formToken)
  )
  const formRolesQueryState = useAppSelector(state => selectFormRolesQueryStateByFormToken(state, formToken))
  const formCooperators = useAppSelector(state => selectFormCooperatorsByFormToken(state, formToken))
  const formRoles = useAppSelector(state => selectFormRolesByFormToken(state, formToken))
  const form = useAppSelector(state => formSelectors.selectById(state.forms, formToken))
  const formBillingAccount = useAppSelector(state => selectFormBillingAccount(state, formToken))

  const isCurrentUserExternal =
    formBillingAccount?.isOrgPlanOrTrial && currentBillingAccountId !== formBillingAccount.id

  const handleReloading = useCallback(async () => {
    setReloading(true)
    await dispatch(fetchFormCooperatorsThunk({ formToken, first: 10, reset: true }))
    setReloading(false)
  }, [formToken, dispatch])

  const handleLoadMore = useCallback(() => {
    if (!formCooperatorsQueryState?.hasNextPage || formCooperatorsQueryState?.isLoading) return
    dispatch(fetchFormCooperatorsThunk({ formToken, first: 10 }))
  }, [dispatch, formCooperatorsQueryState?.hasNextPage, formCooperatorsQueryState?.isLoading, formToken])

  if (
    !formCooperatorsQueryState ||
    !formCooperatorsQueryState.loaded ||
    !formRolesQueryState ||
    !formRolesQueryState.loaded ||
    !form ||
    !formBillingAccount
  ) {
    return (
      <Center className="h-[50vh]">
        <Spinner />
      </Center>
    )
  }

  const handleInviteCooperatorPress = () => {
    if (isCurrentUserExternal) {
      toast.show('你是该企业的外部联系人，当前暂无权限邀请表单的协作者。')
      return
    }
    if ((formCooperatorsQueryState.totalCount ?? 0) >= (formBillingAccount.featureQuotas?.cooperatorLimitQuota ?? 0)) {
      // TODO: click to pay: add_cooperator
    }
    router.push(`/forms/${formToken}/setting/invite_cooperators`)
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: '团队协作',
          ...buildScreenHeaderItems({
            left: headerLeftItems,
            right: [
              {
                type: 'button',
                label: '邀请成员',
                icon: { name: 'person.badge.plus', type: 'sfSymbol' },
                onPress: handleInviteCooperatorPress,
              },
            ],
          }),
        }}
      />
      <FormCooperators
        form={form}
        formBillingAccount={formBillingAccount}
        roles={formRoles}
        cooperators={formCooperators}
        refreshing={reloading}
        onRefresh={handleReloading}
        onLoadMore={handleLoadMore}
      />
    </>
  )
}
